import { useEffect, useRef, useState } from "react";
import { FileText, Download, Paperclip, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getFileUrl, downloadFile, errMsg } from "@/lib/api";
import { fileExt } from "@/lib/format";

const isImage = (ct) => ct && ct.startsWith("image/") && ct !== "image/heic";
export const isVideo = (ct) => ct && ct.startsWith("video/");

export function useSignedUrl(kind, id, enabled = true) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    setUrl(null);
    if (!enabled || !id) return undefined;
    let alive = true;
    getFileUrl(kind, id).then((u) => alive && setUrl(u)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [kind, id, enabled]);
  return url;
}

export function FileThumb({ kind, file, className = "h-16 w-16" }) {
  const img = isImage(file.content_type);
  const vid = isVideo(file.content_type);
  const url = useSignedUrl(kind, file.id, img || vid);
  if (vid && url) {
    return <video src={`${url}#t=0.1`} muted playsInline preload="metadata" className={`${className} object-cover bg-black`} />;
  }
  if (img && url) {
    return <img src={url} alt={file.filename} className={`${className} object-contain bg-ivory-deep border border-white/10/40`} />;
  }
  return (
    <div className={`${className} flex flex-col items-center justify-center bg-ivory-deep border border-white/10/40 text-warm-text`}>
      <FileText className="w-5 h-5" strokeWidth={1.3} />
      <span className="text-[9px] mt-1 tracking-widest">{fileExt(file.filename)}</span>
    </div>
  );
}

export function DownloadButton({ kind, id, testid, label = "Scarica", compact }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await downloadFile(kind, id);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button data-testid={testid} onClick={go} disabled={busy} className={compact ? "btn-link inline-flex items-center gap-1" : "btn-ghost h-9 px-4"}>
      <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> {label}
    </button>
  );
}

export function FilePreviewDialog({ kind, file: current, onClose }) {
  const [last, setLast] = useState(current);
  useEffect(() => { if (current) setLast(current); }, [current]);
  const file = current || last;
  const url = useSignedUrl(kind, file?.id, !!file);
  return (
    <Dialog open={!!current} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="file-preview-dialog" className="max-w-3xl bg-ivory-card rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-normal break-all">{file?.title || file?.filename}</DialogTitle>
          <DialogDescription>{file?.filename}</DialogDescription>
        </DialogHeader>
        <div className="min-h-[200px] max-h-[65vh] overflow-auto flex items-center justify-center bg-ivory-deep border border-white/10/40">
          {!url && <span className="w-8 h-px bg-gold animate-pulse" />}
          {url && isImage(file.content_type) && <img src={url} alt={file.filename} className="max-h-[60vh] object-contain" />}
          {url && isVideo(file.content_type) && <video src={url} controls playsInline className="max-h-[60vh] w-full bg-black" data-testid="file-preview-video" />}
          {url && file.content_type === "application/pdf" && <iframe title="anteprima" src={url} className="w-full h-[60vh]" />}
          {url && !isImage(file.content_type) && !isVideo(file.content_type) && file.content_type !== "application/pdf" && (
            <p className="text-sm text-warm-text p-8">Anteprima non disponibile per questo formato.</p>
          )}
        </div>
        <div className="flex flex-wrap gap-3 justify-end">
          {url && (
            <a data-testid="file-preview-open-link" href={url} target="_blank" rel="noreferrer" className="btn-link inline-flex items-center gap-1 self-center">
              <ExternalLink className="w-3.5 h-3.5" /> Apri in una nuova scheda
            </a>
          )}
          {file && <DownloadButton kind={kind} id={file.id} testid="file-preview-download-btn" />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function FilePicker({ files, setFiles, accept, multiple = true, testid = "file-picker", label = "Allega file" }) {
  const ref = useRef(null);
  const add = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles(multiple ? [...files, ...list] : list.slice(0, 1));
    e.target.value = "";
  };
  return (
    <div>
      <input ref={ref} type="file" accept={accept} multiple={multiple} className="hidden" onChange={add} data-testid={`${testid}-input`} />
      <button type="button" data-testid={`${testid}-button`} onClick={() => ref.current?.click()} className="btn-link inline-flex items-center gap-1.5">
        <Paperclip className="w-3.5 h-3.5" strokeWidth={1.5} /> {label}
      </button>
      {files.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="inline-flex items-center gap-2 bg-ivory-deep border border-white/10/50 px-3 py-1.5 text-xs">
              <span className="max-w-[180px] truncate">{f.name}</span>
              <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label="Rimuovi">
                <X className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
